import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AuthCard } from '@/components/portal/auth-card'
import { Wordmark } from '@/components/portal/wordmark'
import { getResetPasswordPreview, resetPassword } from '@/lib/auth'

export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [email, setEmail] = useState<string | null>(null)
  const [expiresAt, setExpiresAt] = useState<number | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!token) {
      setLoadError('This reset link is invalid.')
      setLoadingPreview(false)
      return
    }

    getResetPasswordPreview(token)
      .then(({ data }) => {
        setEmail(data.email)
        setExpiresAt(data.expiresAt)
      })
      .catch(() => {
        setLoadError(
          'This reset link is invalid or has expired. Request a new one from the sign-in page.',
        )
      })
      .finally(() => setLoadingPreview(false))
  }, [token])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitError(null)

    if (password !== confirmPassword) {
      setSubmitError('Passwords do not match')
      return
    }

    setSubmitting(true)
    try {
      await resetPassword(token, password, confirmPassword)
      navigate('/login?reset=success', { replace: true })
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not reset password')
    } finally {
      setSubmitting(false)
    }
  }

  const expiryLabel =
    expiresAt != null
      ? new Date(expiresAt * 1000).toLocaleString('en-KE', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          hour: 'numeric',
          minute: '2-digit',
        })
      : null

  return (
    <AuthCard>
      <Wordmark portal="Estate Portal" className="mb-8" />

      <h2 className="mb-2 text-center font-serif text-2xl font-medium text-ink">
        Choose a new password
      </h2>
      <p className="mb-6 text-center font-sans text-sm text-muted-foreground">
        Enter a new password for your Riverpark Estate Portal account.
      </p>

      {loadingPreview && (
        <p className="text-center font-sans text-sm text-muted-foreground">Verifying reset link…</p>
      )}

      {loadError && (
        <div className="space-y-4 text-center">
          <p className="font-sans text-sm text-destructive" role="alert">
            {loadError}
          </p>
          <Link
            to="/forgot-password"
            className="font-sans text-sm text-primary hover:text-green-lt"
          >
            Request a new reset link
          </Link>
        </div>
      )}

      {!loadingPreview && !loadError && (
        <form onSubmit={handleSubmit} className="space-y-5">
          {email && <Input label="Email address" type="email" value={email} readOnly disabled />}
          <Input
            label="New password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            hint="At least 8 characters with uppercase, lowercase and a number"
            required
          />
          <Input
            label="Confirm password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            required
          />

          {expiryLabel && (
            <p className="rounded border border-status-pending-bg bg-status-pending-bg/40 px-3 py-2 font-sans text-xs text-status-pending-text">
              This link expires on {expiryLabel} (1 hour from when it was sent).
            </p>
          )}

          {submitError && (
            <p className="font-sans text-sm text-destructive" role="alert">
              {submitError}
            </p>
          )}

          <Button type="submit" className="w-full" size="lg" loading={submitting}>
            Update password
          </Button>
        </form>
      )}
    </AuthCard>
  )
}
